import React, { useState } from 'react';
import { NavigationItem } from '../../../types';
import { SidebarItem } from './SidebarItem';
import { cn } from '../../../utils/cn';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { DESKTOP_NAVIGATION } from '../../../constants/Navigation';
import { useAuthStore } from '../../../store/authStore';

interface SidebarProps {
  className?: string;
}

export const Sidebar: React.FC<SidebarProps> = ({ className }) => {
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(
    new Set(
      DESKTOP_NAVIGATION.filter((item) => item.children).map((item) => item.id)
    )
  );
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);
  const [isHovered, setIsHovered] = useState(false);

  const toggleSection = (sectionId: string) => {
    setCollapsedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  // Determine if sidebar should appear expanded (either not collapsed OR hovered while collapsed)
  const shouldAppearExpanded =
    !isSidebarCollapsed || (isSidebarCollapsed && isHovered);

  // Actual width class
  const widthClass = shouldAppearExpanded ? 'w-[280px]' : 'w-20';

  const user = useAuthStore((state) => state.user);
  const isAdmin = user?.roleName === 'ADMIN' || Number(user?.roleId) === 1 || Number(user?.roleId) === 3;

  const filteredNavigation = DESKTOP_NAVIGATION.filter((item) => {
    if (item.id === 'admin') return isAdmin;
    if (isAdmin && (item.id === 'information-center' || item.id === 'support' || item.id === 'integration')) {
      return false;
    }
    return true;
  });

  return (
    <div
      className={cn(
        'bg-sidebar border-r h-screen flex flex-col transition-all duration-300 relative ',
        widthClass,
        className
      )}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Sidebar Texture */}
      <div
        className={cn(
          'absolute bottom-0 left-0 right-0 h-[60%] pointer-events-none z-0',
          !shouldAppearExpanded ? 'opacity-70' : 'opacity-100'
        )}
      >
        <img
          src="/sidebarTexture.png"
          alt="Sidebar texture"
          className={cn(
            'w-full h-full object-cover object-left-bottom transition-transform duration-300',
            !shouldAppearExpanded ? 'scale-110' : 'scale-100'
          )}
          style={{
            transformOrigin: 'left bottom',
          }}
        />
      </div>

      {/* Header */}
      <div className="p-4 relative z-10 flex flex-col items-center">
        <img
          src={
            shouldAppearExpanded
              ? '/sidebarLogo.svg'
              : '/sidebarLogoCollapsed.svg'
          }
          alt="Logo"
          className="transition-opacity duration-300 h-10"
        />
        <div className="mt-4 px-5 border border-[#DDDDDD66] w-full"></div>
      </div>

      {/* Navigation */}
      <nav
        className={cn(
          'flex-1 overflow-y-auto overflow-x-hidden p-4 space-y-2 transition-all duration-300 relative z-10 no-scrollbar',
          shouldAppearExpanded ? 'px-4' : 'px-2'
        )}
      >
        {filteredNavigation.map((item) => (
          <SidebarItem
            key={item.id}
            item={item}
            collapsedSections={collapsedSections}
            onToggleSection={toggleSection}
            level={0}
            isSidebarCollapsed={!shouldAppearExpanded}
          />
        ))}
      </nav>
    </div>
  );
};
